import type { ReactNode } from "react";
import { Widget, WidgetSkeleton } from "@nexus/widget-sdk";

import { SectionLayout } from "@/components/layout/SectionLayout";
import { WORKSPACE_LABEL } from "@/config/uiConfig";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { DashboardRecordContext } from "@/pages/live/DashboardRecordContext";
import { useDashboardPlacement } from "./DashboardLayout";

/** Where a dashboard will appear, before its record is known: KPI strip, then two charts. */
function DashboardPlaceholder() {
  return (
    <div className="density-dashboard-grid grid grid-cols-12" aria-busy="true">
      <div className="col-span-12">
        <Widget hideHeader>
          <WidgetSkeleton shape="kpis" label="Loading dashboard" />
        </Widget>
      </div>
      {[0, 1].map((index) => (
        <div key={index} className="col-span-12 min-h-72 lg:col-span-6">
          <Widget hideHeader>
            <WidgetSkeleton shape="chart" label="Loading dashboard" />
          </Widget>
        </div>
      ))}
    </div>
  );
}

/** No scope control: the dashboard decides its own, and none is known yet. */
const NO_SCOPE_CONTROLS = [] as const;

/**
 * The dashboard page shell while its record loads, or with a page-level state
 * (error, empty) in place of the dashboard. Unknown loading titles use a skeleton.
 *
 * Scope controls (tenant, time range) belong to the dashboard's own viewer
 * settings. Until the record is known they are not shown at all — the section
 * defaults would flash in and vanish when the dashboard (or its absence) turns
 * out to have none. A `record` already known from a cached listing shows its
 * own controls at once.
 */
export function DashboardRouteState({
  title,
  record,
  children,
  quiet = false,
}: {
  title?: string;
  record?: DashboardRecord;
  children?: ReactNode;
  quiet?: boolean;
}) {
  const { section, sectionTitle, sidebarItems } = useDashboardPlacement();
  const loading = children === undefined;
  const pageTitle = title || (loading ? "Loading dashboard" : "Dashboard");
  const known = loading ? record : undefined;
  const shell = (
    <SectionLayout
      sectionTitle={sectionTitle}
      sidebarItems={sidebarItems}
      title={pageTitle}
      titleLoading={loading && !title && !quiet}
      hideHeader={loading && quiet && !known}
      breadcrumbTrail={
        section === "workspace"
          ? [
              { label: WORKSPACE_LABEL, href: "/workspace/dashboards" },
              { label: "Dashboards", href: "/workspace/dashboards" },
              { label: pageTitle },
            ]
          : undefined
      }
      variant="dashboard"
      previewFilters={known ? undefined : NO_SCOPE_CONTROLS}
    >
      {children ??
        (quiet ? (
          <div role="status" aria-label="Loading dashboard" aria-busy="true" className="sr-only" />
        ) : (
          <DashboardPlaceholder />
        ))}
    </SectionLayout>
  );
  return known ? <DashboardRecordContext value={known}>{shell}</DashboardRecordContext> : shell;
}
